import { useState } from 'react'
import { anzahlHausbegriffe, SPRACHEN, t, ZAHLENFORMATWAHL, zahlenbeispiel, anzahlwort } from './sprache'
import type { Zahlenformat } from './sprache'
import { api } from './api'
import { Abrufausfall, useAbruf } from './Abruf'
import { fachlicherSatz } from './Fehlersatz'
import type { Haussprache } from './typen'
import { de } from './sprache/de'
import { en } from './sprache/en'
import { useDarf } from './Rechte'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'

/**
 * *Betrieb → Sprachen und Begriffe des Hauses.*
 *
 * Zwei Dinge, die auseinandergehalten gehören und in vielen Systemen
 * zusammenfallen:
 *
 *  - **Die Sprache** ist die Wahl des Anwenders. Sie wirkt sofort und wird für
 *    ihn gemerkt — die Auslandstochter meldet ihre Daten selbst und soll sich
 *    nicht durch eine deutsche Oberfläche klicken.
 *  - **Die Begriffe des Hauses** sind die Wahl des Kunden. Sie legen eine
 *    Schicht über jede Sprache und ändern die Beschriftung, nicht die
 *    Bedeutung (R148, R157).
 *
 * Der eigene Name jeder Sprache steht in der Sprache selbst — „Deutsch" und
 * „English", nicht „Deutsch" und „Englisch". Wer die Oberfläche nicht lesen
 * kann, findet seine Sprache sonst nicht in der Liste.
 */
const NAMEN: Record<string, string> = {
  de: de['sprache.name'],
  en: en['sprache.name'],
}

export function Sprachen({
  gewaehlt,
  zahlenformat,
  aufWechsel,
  aufZahlenformat,
}: {
  gewaehlt: string
  zahlenformat: Zahlenformat
  aufWechsel: (code: string) => void
  aufZahlenformat: (wahl: Zahlenformat) => void
}) {
  return (
    <section>
      <h1>{t('bereich.betrieb.sprachen')}</h1>

      <h2 className="abschnitt">{t('sprachen.gewaehlt')}</h2>
      <div className="sprachwahl" role="radiogroup" aria-label={t('sprachen.gewaehlt')}>
        {SPRACHEN.map((code) => (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={code === gewaehlt}
            className={code === gewaehlt ? 'aktiv' : ''}
            onClick={() => aufWechsel(code)}
          >
            <span className="code">{code.toUpperCase()}</span>
            <span>{NAMEN[code] ?? code}</span>
          </button>
        ))}
      </div>

      <h2 className="abschnitt">{t('zahlenformat.titel')}</h2>
      <div className="sprachwahl" role="radiogroup" aria-label={t('zahlenformat.titel')}>
        {ZAHLENFORMATWAHL.map((wahl) => (
          <button
            key={wahl}
            type="button"
            role="radio"
            aria-checked={wahl === zahlenformat}
            className={wahl === zahlenformat ? 'aktiv' : ''}
            onClick={() => aufZahlenformat(wahl)}
          >
            {/* Die Zahl selbst ist die Beschriftung — sie erklärt sich. */}
            <span className="zahl">{zahlenbeispiel(wahl)}</span>
            {wahl === 'sprache' && <span className="code">{t('zahlenformat.sprache')}</span>}
          </button>
        ))}
      </div>

      {/*
        **Die Sprachen des Hauses** (7.4 Abschnitt 18.6, R556).

        Der Abschnitt darüber ist die Wahl **eines Anwenders**; dieser hier ist
        die Entscheidung **des Hauses**. Sie fielen bis zum 2026-09-16
        zusammen, und zwar nirgends: Eine Sprache entstand, indem jemand an
        einem Stammdatenblatt `pl` in ein freies Feld tippte.

        **Die beiden Ebenen stehen hier zum ersten Mal nebeneinander.**
        Stammdaten wirken sofort — es sind Daten des Hauses. Ob die Bedienung
        vorliegt, hängt an der Sprachdatei (ADR 010), und das weiß diese Seite
        und nicht die Datenbank: Sie vergleicht das Register mit ihrem eigenen
        Vorrat, statt einen Schalter zu glauben, der im Backend gepflegt wird.
      */}
      <h2 className="abschnitt">{t('sprachen.haus')}</h2>
      <Sprachregister />

      <h2 className="abschnitt">{t('sprachen.beschriftungen.titel')}</h2>
      <p className="erlaeuterung">
        {t('sprachen.beschriftungen.stand', { anzahl: anzahlwort(anzahlHausbegriffe(gewaehlt)) })}
      </p>
    </section>
  )
}

/**
 * Die Periode, ab der eine Stilllegung gilt — der laufende Monat.
 *
 * **Nicht wählbar, und das ist Absicht:** Eine Sprache rückwirkend
 * stillzulegen hieße, gepflegten Texten nachträglich ihren Entstehungsgrund zu
 * nehmen. Wer ab heute nicht mehr pflegt, legt ab heute still.
 */
function heutigePeriode(): string {
  const jetzt = new Date()
  return `${jetzt.getFullYear()}${String(jetzt.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Das Sprachregister des Hauses (7.4 Abschnitt 18.6, R556).
 *
 * **Eine Sprache ist ein Stammdatum wie jedes andere** — Fassungsreihe,
 * Stilllegung, eigene Kennung, eigenes Blatt in der Austauschmappe. Gepflegt
 * wird sie trotzdem hier und nicht unter *Stammdaten*: Ein Ort, ein Weg
 * (Entscheidung vom 2026-09-16). Der Katalog gibt ihr die Bauform, dieser
 * Bildschirm den Platz.
 */
function Sprachregister() {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('sprachen:sprachregister')

  // **Das Register ist der Inhalt dieser Tabelle — ein Hauptabruf** (7.2
  // Abschnitt 39, R1211). Bis zum 2026-10-06 wurde ein Ausfall zur leeren
  // Liste, und darunter stand der Satz, das Haus führe keine Sprache.
  const registerabruf = useAbruf(() => api.sprachregister(), [])
  const register: Haussprache[] = registerabruf.wert ?? []
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [eigenname, setEigenname] = useState('')
  const darf = useDarf()

  async function fuehreAus(tuwas: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)
    try {
      await tuwas()
      registerabruf.erneut()
    } catch (fehler) {
      // **Meldung und Handreichung zusammen** (R145): Was festgestellt wurde,
      // und was der Anwender dagegen tun kann. Eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  // **Der Code sieht aus wie ein Sprachcode oder er ist keiner** (R145): ISO
  // 639-1, bei Bedarf mit Region. Dieselbe Form, die die Datenbank verlangt —
  // hier steht sie, damit der Knopf gar nicht erst zulässt, was dort scheitert.
  const codeGueltig = /^[a-z]{2}(-[a-z]{2})?$/.test(code.trim().toLowerCase())
  const schonDa = register.some((s) => s.code === code.trim().toLowerCase())

  return (
    <>
      {meldung && <p className="hinweis">{meldung}</p>}

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="sprachregister ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('sprachen.spalte.sprache')}</th>
              <th>{t('sprachen.spalte.stammdaten')}</th>
              <th>{t('sprachen.spalte.bedienung')}</th>
              <th>{t('sprachen.spalte.reihenfolge')}</th>
              {darf('stammdatumPflegen') && <th />}
            </tr>
          </thead>
          <tbody>
            {/* „Keine Sprache" nur, wenn das Register kam und leer ist (R1210). */}
            {registerabruf.ausgefallen ? (
              <tr>
                <td colSpan={5}>
                  <Abrufausfall abruf={registerabruf} />
                </td>
              </tr>
            ) : (
              registerabruf.wert?.length === 0 && (
                <tr>
                  <td colSpan={5} className="erlaeuterung">{t('sprachen.leer')}</td>
                </tr>
              )
            )}
            {register.map((s) => (
              <tr key={s.schluessel} className={s.istStillgelegt ? 'stillgelegt' : undefined}>
                <td>
                  <span className="code">{s.code.toUpperCase()}</span> {s.eigenname}
                  {s.istStillgelegt && (
                    <span className="merkmal">{t('sprachen.stillgelegt')}</span>
                  )}
                </td>
                <td>{s.istStammdatensprache ? t('sprachen.ja') : t('sprachen.nein')}</td>
                {/*
                  **Abgeleitet und nicht gespeichert.** „Französisch als
                  Stammdatensprache geht heute Nachmittag; als
                  Oberflächensprache mit der nächsten Auslieferung" — das Blatt
                  sagt es, statt eine halb französische Oberfläche zu zeigen.
                */}
                <td>
                  {SPRACHEN.includes(s.code)
                    ? t('sprachen.bedienung.mitdatei')
                    : t('sprachen.bedienung.ohnedatei')}
                </td>
                <td className="zahl">{s.sortierung ?? '—'}</td>
                {/* Ohne Recht zum Pflegen fehlt die Spalte der beiden Knöpfe (R1180). */}
                {darf('stammdatumPflegen') && (
                <td>
                  <button
                    type="button"
                    disabled={laeuft || s.istStillgelegt}
                    onClick={() =>
                      fuehreAus(() =>
                        api.stammdatumPflegen('sprache', s.schluessel, {
                          felder: {
                            ist_stammdatensprache: s.istStammdatensprache ? 'false' : 'true',
                          },
                        }),
                      )
                    }
                  >
                    {/* **Der Knopf sagt, was er tut, und nicht, was danach
                        dasteht** (R143): "ja | ... | nein" nebeneinander liest
                        sich wie ein Widerspruch. Im Lauf am eigenen Bildschirm
                        gesehen. */}
                    {s.istStammdatensprache
                      ? t("sprachen.nichtmehrfuehren")
                      : t("sprachen.fuehren")}
                  </button>
                  {/*
                    **Stillgelegt, nicht gelöscht** (R316) — und der Weg zurück
                    steht daneben. Ohne ihn wäre die Absage „nehmen Sie die
                    Stilllegung unter Betrieb → Sprachen zurück" ein Verweis auf
                    einen Handgriff, den es nicht gibt (R451).

                    **Leer heißt: nicht stillgelegt.** Der Schreibweg macht aus
                    einem leeren Wert ein NULL (R242) — dieselbe Rückrichtung
                    wie an jedem vererbbaren Feld.
                  */}
                  <button
                    type="button"
                    disabled={laeuft}
                    onClick={() =>
                      fuehreAus(() =>
                        api.stammdatumPflegen('sprache', s.schluessel, {
                          felder: { stillgelegt_ab: s.istStillgelegt ? '' : heutigePeriode() },
                        }),
                      )
                    }
                  >
                    {s.istStillgelegt ? t('sprachen.aufnehmen') : t('sprachen.stilllegen')}
                  </button>
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {darf('stammdatumAnlegen') && (
      <div className="neuesprache">
        <label htmlFor="sprachcode">{t('sprachen.spalte.sprache')}</label>
        <input
          id="sprachcode"
          value={code}
          maxLength={5}
          placeholder="fr"
          disabled={laeuft}
          onChange={(e) => setCode(e.target.value)}
        />
        <input
          aria-label={t('sprachen.spalte.sprache')}
          value={eigenname}
          placeholder="Français"
          disabled={laeuft}
          onChange={(e) => setEigenname(e.target.value)}
        />
        <button
          type="button"
          disabled={laeuft || !codeGueltig || schonDa || eigenname.trim().length === 0}
          onClick={() =>
            fuehreAus(async () => {
              await api.stammdatumAnlegen('sprache', code.trim().toLowerCase(), {
                felder: {
                  eigenname: eigenname.trim(),
                  ist_stammdatensprache: 'true',
                  // Hinten anstellen: Die Reihenfolge ist die Rückfallkette,
                  // und eine neue Sprache schiebt sich nicht vor die, in denen
                  // das Haus seit je pflegt.
                  sortierung: String((register.length + 1) * 10),
                },
              })
              setCode('')
              setEigenname('')
            })
          }
        >
          {t('stammdaten.sprache.anlegen')}
        </button>
      </div>
      )}
    </>
  )
}
